import {
    connect as reduxConnect,
    InferableComponentEnhancerWithProps,
    MapDispatchToPropsNonObject,
    MapStateToPropsParam,
} from 'react-redux';
import {
    Action,
    ActionCreatorsMapObject,
    bindActionCreators as _bindActionCreators,
    Dispatch,
} from 'redux';
import { ThunkAction } from 'redux-thunk';

import { GlobalState } from 'reactApp/core/rootReducer';

export type TypeOfConnector<T> = T extends InferableComponentEnhancerWithProps<
    infer Props,
    infer _
>
    ? Props
    : never;

export type GetFunctionResult<T> = T extends (...arg: infer Args) => infer R
    ? R
    : never;

export const unboxThunk = <Args extends any[], R, S, E, A extends Action>(
    thunkFn: (...args: Args) => ThunkAction<R, S, E, A>,
) => (thunkFn as any) as CutMiddleFunction<typeof thunkFn>;

// when use it instead of copypaste content of this function to connector - webstorm can't get actions in props...
export const makeDispatchToPropsMapper = (actions: ActionCreatorsMapObject) => (
    dispatch: Dispatch<Action>,
) => bindActionCreators(actions, dispatch);

export type ThunkResult<T> = ThunkAction<T, GlobalState, null, Action>;
export type ThunkResultAsync<T> = ThunkAction<
    Promise<T>,
    GlobalState,
    null,
    Action
>;

export type Selector<T> = (state: GlobalState) => T;

export const bindActionCreators = <T extends ActionCreatorsMapObject>(
    actions: T,
    dispatch: Dispatch<Action>,
) => {
    return (_bindActionCreators(actions, dispatch) as any) as {
        [P in keyof T]: CutMiddleFunction<T[P]>;
    };
};

export const makeConnector = <
    TOwnProps = {},
    TStateProps = {},
    TActions extends ActionCreatorsMapObject = {}
>(
    mapStateToProps: MapStateToPropsParam<TStateProps, TOwnProps, GlobalState>,
    actions: ((ownProps) => TActions) | TActions = {} as TActions,
) => {
    return connect(mapStateToProps, (dispatch: Dispatch<Action>, ownProps) => {
        return bindActionCreators(
            typeof actions === 'function' ? actions(ownProps) : actions,
            dispatch,
        );
    });
};

export function connect<
    TStateProps = {},
    TDispatchProps = {},
    TOwnProps = {},
    State = {}
>(
    mapStateToProps: MapStateToPropsParam<TStateProps, TOwnProps, State>,
    mapDispatchToProps: MapDispatchToPropsNonObject<
        TDispatchProps,
        TOwnProps
    > = {} as MapDispatchToPropsNonObject<TDispatchProps, TOwnProps>,
    mergeProps = null,
) {
    return reduxConnect(mapStateToProps, mapDispatchToProps, mergeProps, {
        pure: false,
    });
}
